import React, { useCallback, useEffect, useState } from "react";
import VideoContainerView from "../selfView/VideoContainerView";
import Button from "src/common/Button/Button";
import { useTwilioUserMediaContext } from "../../call-sdk/context/TwilioUserMediaContext";
import { useNavigate } from "react-router-dom";
import ErrorBoundary from "../ErrorBoundary";
import HelpScreenDialog from "./HelpScreen/HelpScreenDialog";
import { useCall } from "src/call-sdk/context/CallContext";
import {
  PermisionContainer,
  AccessContainer,
  ButtonContainer,
  HelpContainer,
} from "./PermissionStyles";
import Typography from "@mui/material/Typography";
import StorageConstants from "src/config/storage.constants";
import LocalStorage from "src/call-sdk/lib/local-storage";
import * as Sentry from "@sentry/react";

const Permission = () => {
  const {
    requestMediaPermission,
    reRequestMediaPermission,
    audioDeviceId,
    videoDeviceId,
    isMediaPermissionGranted,
    isMediaPermissionDenied,
    isMediaPermissionDeniedByUser,
    destroyMedia,
  } = useTwilioUserMediaContext();
  const navigate = useNavigate();
  const [helpClicked, setHelpClicked] = useState(false);
  const call = useCall();
  const isHUUser = Boolean(LocalStorage.get(StorageConstants.IS_HU_USER));

  const handleRequestPermission = useCallback(() => {
    if (!isMediaPermissionGranted) {
      requestMediaPermission({
        type: "ideal",
        audioDeviceId: audioDeviceId ?? "",
        ...(!isHUUser && { videoDeviceId: videoDeviceId ?? "" }),
      });
    }
  }, [
    audioDeviceId,
    isMediaPermissionGranted,
    requestMediaPermission,
    videoDeviceId,
    isHUUser,
  ]);

  const handleReRequest = useCallback(() => {
    reRequestMediaPermission({
      type: "ideal",
      audioDeviceId: audioDeviceId ?? "",
      ...(!isHUUser && { videoDeviceId: videoDeviceId ?? "" }),
    });
  }, [audioDeviceId, reRequestMediaPermission, videoDeviceId, isHUUser]);

  // Function to check if the device is iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const handleIOSpermission = () => {
    destroyMedia();
    call.destroy();
    navigate({ pathname: `/qr-reconnecting` }, { replace: true });
  };

  useEffect(() => {
    changeAddressBarColor();
    if (isMediaPermissionGranted) {
      navigate({ pathname: "/qr-connecting" }, { replace: true });
    } else if (isMediaPermissionDenied) {
      Sentry.captureMessage("user denied permissions ");
    } else {
      handleRequestPermission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaPermissionGranted, isMediaPermissionDenied, navigate]);

  const changeAddressBarColor = () => {
    let metas = document.head.getElementsByTagName("meta");
    metas[2].content = "#000000";
    document.body.style.backgroundColor = "#000000";
  };

  const handleHelpClose = () => {
    setHelpClicked(false);
  };

  const handleHelpOpen = () => {
    setHelpClicked(true);
  };

  return (
    <ErrorBoundary>
      <PermisionContainer>
        {!isHUUser && <VideoContainerView />}
        {(!isMediaPermissionGranted || isMediaPermissionDenied) && (
          <AccessContainer>
            {isMediaPermissionDeniedByUser && (
              <div>
                <p className="oops">Oops!</p>
                <p className="instruction">
                  Please try again by allowing access to your
                  {!isHUUser && " camera and"}&nbsp;microphone
                </p>
              </div>
            )}
            <ButtonContainer>
              {isMediaPermissionDeniedByUser && (
                <Button
                  onClick={() => {
                    if (isIOS()) {
                      handleIOSpermission();
                    } else {
                      handleReRequest();
                    }
                  }}
                  aria-label="Try Again"
                  fullWidth
                >
                  Try Again
                </Button>
              )}
            </ButtonContainer>
            {isMediaPermissionDeniedByUser && (
              <HelpContainer onClick={handleHelpOpen}>
                <Typography
                  style={{ color: "#1884EB", fontSize: "20px" }}
                  aria-label="Need Help"
                >
                  Need Help?
                </Typography>
              </HelpContainer>
            )}
          </AccessContainer>
        )}
      </PermisionContainer>

      <HelpScreenDialog open={helpClicked} onClose={handleHelpClose} />
    </ErrorBoundary>
  );
};

export default Permission;
